import { AFFIXES, COMPONENTS } from "../content/affixes";
import { activeAffixChips } from "../content/affix-identity";
import { frameCargoDamageBonus, SHIP_FRAMES } from "../content/ships";
import { progressiveHelpLine, shouldCollapseBindings } from "../content/onboarding";
import type { BankedProfile } from "../simulation/persistence";
import { currentObjective } from "../simulation/sortie-objective";
import { activeSortieContract, contractHudValue } from "../simulation/sortie-contracts";
import { formatDeathDetail, formatDeathHeadline } from "../simulation/death-report";
import { debriefHeroCopy } from "../simulation/run-debrief";
import type { WeaponHudSlot, WorldSnapshot } from "../simulation/snapshot";
import { resolveHudPresentation } from "./hud-state";

const SLOT_SHAPE: Record<WeaponHudSlot["slot"], string> = {
  primary: "▮",
  secondary: "◆",
  ordnance: "▲",
};

type VitalsEls = {
  shield: HTMLElement;
  armor: HTMLElement;
  hull: HTMLElement;
  boost: HTMLElement;
  extract: HTMLElement;
};

type RunChipEls = {
  root: HTMLElement;
  label: HTMLElement;
  value: HTMLElement;
  last: string;
};

export class HudController {
  private railBuilt = false;
  private vitalsBuilt = false;
  private vitals: VitalsEls | null = null;
  private runChipEls: RunChipEls[] | null = null;
  private lastScore = Number.NaN;
  private lastScoreText = "";
  private lastBandText = "";
  private lastLevelText = "";
  private lastXpText = "";
  private lastXpPct = Number.NaN;
  private lastMessage = "";
  private lastHelp = "";
  private lastHelpCollapsed: boolean | null = null;
  private lastStreakText = "";
  private lastAffixKey = "";
  private lastVitalsKey = "";
  private lastDisruption = "";
  private lastIncursion = false;
  private lastIncursionLabel = "";
  private lastCarrierLabel = "";
  private lastCarrierDeg = Number.NaN;
  private lastOutcomeKey = "";
  private lastVignetteActive = false;
  private lastObjectiveKey = "";
  private readonly objectiveEl = document.querySelector<HTMLElement>("#objective");

  public constructor(
    private readonly root: {
      combatHud: HTMLElement;
      status: HTMLElement;
      vitals: HTMLElement;
      inventory: HTMLElement;
      band: HTMLElement;
      help: HTMLElement;
      message: HTMLElement;
      levelBadge: HTMLElement;
      xpBar: HTMLElement;
      xpLabel: HTMLElement;
      score: HTMLElement;
      streak: HTMLElement;
      jammer: HTMLElement;
      hitVignette: HTMLElement;
      carrierNav: HTMLElement;
      affixChips: HTMLElement;
      loadoutRail: HTMLElement;
      runChips: HTMLElement;
      softLockReticle: HTMLElement;
      outcomeBeat: HTMLElement;
      incursion?: HTMLElement;
    },
  ) {
    // Legacy inventory dump stays hidden; help becomes progressive one-liners.
    this.root.inventory.hidden = true;
  }

  public render(
    snap: WorldSnapshot,
    banked: BankedProfile,
    opts: { debriefHold?: boolean } = {},
  ): void {
    const bandText = `${snap.biomeName.toUpperCase()} · G${snap.threatGrade}`;
    if (bandText !== this.lastBandText) {
      this.root.band.textContent = bandText;
      this.lastBandText = bandText;
    }
    this.root.band.dataset.danger = String(snap.dangerTier);
    this.root.band.title = `${snap.biomeName} · ${snap.dangerLabel} zone · Threat G${snap.threatGrade}`;

    const prog = snap.runProgress;
    const xpPct = prog.xpToNext > 0 ? (prog.xp / prog.xpToNext) * 100 : prog.level >= 25 ? 100 : 0;
    const levelText = `LV ${prog.level}`;
    if (levelText !== this.lastLevelText) {
      this.root.levelBadge.textContent = levelText;
      this.lastLevelText = levelText;
    }
    const xpClamped = clampPct(xpPct);
    if (xpClamped !== this.lastXpPct) {
      this.root.xpBar.style.setProperty("--xp", `${xpClamped}%`);
      this.lastXpPct = xpClamped;
    }
    const xpText =
      prog.xpToNext > 0
        ? `${prog.xp} / ${prog.xpToNext} XP`
        : prog.level >= 25
          ? "MAX"
          : `${prog.xp} XP`;
    if (xpText !== this.lastXpText) {
      this.root.xpLabel.textContent = xpText;
      this.lastXpText = xpText;
    }
    if (prog.score !== this.lastScore) {
      this.lastScoreText = prog.score.toLocaleString();
      this.root.score.textContent = this.lastScoreText;
      this.lastScore = prog.score;
    }

    const streakMult = 1 + prog.killStreak * 0.05;
    if (prog.killStreak >= 3) {
      const streakText = `×${streakMult.toFixed(1)}`;
      this.root.streak.hidden = false;
      if (streakText !== this.lastStreakText) {
        this.root.streak.textContent = streakText;
        this.lastStreakText = streakText;
      }
    } else if (!this.root.streak.hidden) {
      this.root.streak.hidden = true;
      this.lastStreakText = "";
    }

    this.renderVitals(snap);
    this.renderRunChips(snap);
    this.renderObjective(snap, banked);
    this.renderWeaponRail(snap);
    this.renderLoadoutChips(snap);
    this.renderSoftLockReticle(snap);

    const message = snap.bossObjective && !snap.bossBanner ? snap.bossObjective : snap.message;
    if (message !== this.lastMessage) {
      this.root.message.textContent = message;
      this.lastMessage = message;
    }
    this.renderHelp(snap, banked);

    this.renderCarrierNav(snap);
    this.renderOutcomeBeat(snap, opts.debriefHold === true);

    const disruption = snap.shieldJammed
      ? snap.guidanceSuppressed
        ? "both"
        : "shield"
      : snap.guidanceSuppressed
        ? "guidance"
        : "";
    if (disruption !== this.lastDisruption) {
      this.root.jammer.hidden = disruption === "";
      const label = this.root.jammer.querySelector<HTMLElement>(".jammer-text > span:first-child");
      const detail = this.root.jammer.querySelector<HTMLElement>(".jammer-sub");
      if (label) label.textContent = disruption === "guidance" ? "NULL" : "JAM";
      if (detail) {
        detail.textContent =
          disruption === "both"
            ? "SHIELD + GUIDANCE OFF"
            : disruption === "guidance"
              ? "GUIDANCE OFF"
              : "SHIELD REGEN OFF";
      }
      this.root.jammer.setAttribute(
        "aria-label",
        disruption === "both"
          ? "Jammer and Nullifier nearby — shield regeneration and guidance suppressed"
          : disruption === "guidance"
            ? "Nullifier nearby — projectile guidance suppressed"
            : "Jammer nearby — shield regeneration suppressed",
      );
      this.lastDisruption = disruption;
    }
    this.root.jammer.dataset.colorblind = snap.settings.colorblindTelegraphs ? "true" : "false";

    const incursionEl = this.root.incursion;
    if (incursionEl) {
      const bossBanner = snap.bossBanner;
      const showIncursion = snap.incursionActive;
      const showBoss = !showIncursion && Boolean(bossBanner && bossBanner.timer > 0);
      const bannerVisible = showIncursion || showBoss;
      if (bannerVisible !== this.lastIncursion) {
        incursionEl.hidden = !bannerVisible;
        this.lastIncursion = bannerVisible;
      }
      if (showIncursion) {
        delete incursionEl.dataset.tone;
        const label = snap.incursionLabel || "INCURSION";
        if (label !== this.lastIncursionLabel) {
          incursionEl.textContent = label;
          this.lastIncursionLabel = label;
        }
        incursionEl.style.setProperty("--inc-timer", String(snap.incursionTimer));
      } else if (showBoss && bossBanner) {
        if (incursionEl.dataset.tone !== bossBanner.tone) {
          incursionEl.dataset.tone = bossBanner.tone;
        }
        if (bossBanner.label !== this.lastIncursionLabel) {
          incursionEl.textContent = bossBanner.label;
          this.lastIncursionLabel = bossBanner.label;
        }
        incursionEl.style.setProperty("--inc-timer", String(bossBanner.timer));
      } else if (incursionEl.dataset.tone) {
        delete incursionEl.dataset.tone;
      }
    }

    const presentation = resolveHudPresentation(snap);
    this.root.combatHud.hidden = !presentation.showCombatHud;
    if (presentation.showCombatHud) {
      this.root.combatHud.dataset.primary = presentation.primary;
    } else {
      delete this.root.combatHud.dataset.primary;
    }
    if (this.objectiveEl) {
      this.objectiveEl.hidden = !presentation.showObjectiveDetail;
      this.objectiveEl.dataset.detailOnly = presentation.primary === "alert" ? "true" : "false";
    }
    if (incursionEl) incursionEl.hidden = presentation.primary !== "alert";
    this.root.message.hidden = !presentation.showMessage;
    if (!presentation.showCarrierNavigation) this.root.carrierNav.hidden = true;

    const vignette = snap.hitVignette;
    if (vignette && vignette.intensity > 0.02) {
      const deg = 90 - (vignette.bearing * 180) / Math.PI;
      this.root.hitVignette.dataset.active = "true";
      this.root.hitVignette.style.setProperty("--bearing", `${deg}deg`);
      this.root.hitVignette.style.setProperty("--intensity", String(vignette.intensity * 0.85));
      this.lastVignetteActive = true;
    } else if (this.lastVignetteActive) {
      delete this.root.hitVignette.dataset.active;
      this.root.hitVignette.style.removeProperty("--intensity");
      this.lastVignetteActive = false;
    }

    // Legacy full-screen status only when no outcome beat / debrief.
    this.root.status.hidden = true;
  }

  private ensureVitals(): VitalsEls {
    if (this.vitals && this.vitalsBuilt) return this.vitals;
    this.root.vitals.innerHTML = `
      <div class="bar shield" style="--v:0%"><span>SHD</span></div>
      <div class="bar armor" style="--v:0%" hidden><span>ARM</span></div>
      <div class="bar hull" style="--v:0%"><span>HULL</span></div>
      <div class="bar boost" style="--v:0%"><span>BST</span></div>
      <div class="bar extract" style="--v:0%" hidden><span>EXT</span></div>
    `;
    this.vitals = {
      shield: this.root.vitals.querySelector(".bar.shield")!,
      armor: this.root.vitals.querySelector(".bar.armor")!,
      hull: this.root.vitals.querySelector(".bar.hull")!,
      boost: this.root.vitals.querySelector(".bar.boost")!,
      extract: this.root.vitals.querySelector(".bar.extract")!,
    };
    this.vitalsBuilt = true;
    return this.vitals;
  }

  private renderVitals(snap: WorldSnapshot): void {
    const els = this.ensureVitals();
    const shieldPct = clampPct((snap.player.shield / Math.max(1, snap.player.maxShield)) * 100);
    const hullPct = clampPct((snap.player.hull / Math.max(1, snap.player.maxHull)) * 100);
    const armorPct =
      snap.player.maxArmor > 0 ? clampPct((snap.player.armor / snap.player.maxArmor) * 100) : 0;
    const boostPct = clampPct(snap.player.boostFuel * 100);
    const extractPct = snap.phase === "extracting" ? clampPct(snap.extractProgress * 100) : 0;
    const key = `${shieldPct}|${hullPct}|${armorPct}|${boostPct}|${extractPct}|${snap.player.maxArmor}|${snap.phase}`;
    if (key === this.lastVitalsKey) return;
    this.lastVitalsKey = key;

    els.shield.style.setProperty("--v", `${shieldPct}%`);
    els.hull.style.setProperty("--v", `${hullPct}%`);
    els.boost.style.setProperty("--v", `${boostPct}%`);
    els.armor.hidden = snap.player.maxArmor <= 0;
    if (snap.player.maxArmor > 0) els.armor.style.setProperty("--v", `${armorPct}%`);
    els.extract.hidden = snap.phase !== "extracting";
    if (snap.phase === "extracting") els.extract.style.setProperty("--v", `${extractPct}%`);
  }

  private renderHelp(snap: WorldSnapshot, banked: BankedProfile): void {
    const collapsed = shouldCollapseBindings({
      runsCompleted: banked.runsCompleted,
      weaponLiteracyCoached: banked.weaponLiteracyCoached,
    });
    this.root.help.hidden = snap.phase !== "onboarding" && banked.runsCompleted > 0;
    if (collapsed !== this.lastHelpCollapsed) {
      this.root.help.classList.toggle("help--truncated", collapsed);
      this.lastHelpCollapsed = collapsed;
    }
    const help = progressiveHelpLine({
      runsCompleted: banked.runsCompleted,
      weaponLiteracyCoached: banked.weaponLiteracyCoached,
      onboardingStep: snap.onboardingStep,
      phase: snap.phase,
    });
    if (help !== this.lastHelp) {
      this.root.help.textContent = help;
      this.lastHelp = help;
    }
  }

  private ensureRunChips(): RunChipEls[] {
    if (this.runChipEls) return this.runChipEls;
    const defs = [
      { label: "SCRAP", tone: "salvage" },
      { label: "CORE", tone: "core" },
      { label: "GRADE", tone: "grade" },
      { label: "REDLINE", tone: "danger" },
      { label: "RAID", tone: "boost" },
    ] as const;
    this.root.runChips.replaceChildren();
    this.runChipEls = defs.map((chip) => {
      const el = document.createElement("span");
      el.className = "run-chip";
      el.dataset.tone = chip.tone;
      const em = document.createElement("em");
      em.textContent = chip.label;
      const strong = document.createElement("strong");
      el.append(em, strong);
      this.root.runChips.appendChild(el);
      return { root: el, label: em, value: strong, last: "" };
    });
    return this.runChipEls;
  }

  private renderRunChips(snap: WorldSnapshot): void {
    const inRun =
      snap.phase === "expedition" ||
      snap.phase === "onboarding" ||
      snap.phase === "extracting" ||
      snap.phase === "launching";
    if (!inRun) {
      this.root.runChips.hidden = true;
      return;
    }
    this.root.runChips.hidden = false;
    const chips = this.ensureRunChips();
    const intel = snap.runProgress.takenNodes.includes("intel_threat_analyzer");
    const contract = activeSortieContract(snap.activeSortieContract);
    chips[3]!.root.hidden = contract == null;
    chips[3]!.label.textContent = contract?.hudLabel ?? "CONTRACT";
    const frame = SHIP_FRAMES[snap.player.frameId] ?? SHIP_FRAMES.skimmer!;
    const raidBonus = frameCargoDamageBonus(frame, snap.runInventory.salvage);
    chips[4]!.root.hidden = frame.id !== "corsair";
    const values = [
      String(snap.runInventory.salvage),
      String(snap.runInventory.eliteCores),
      intel ? `${snap.threatBudget}/${snap.threatBudgetMax}` : `G${snap.threatGrade}`,
      contract ? contractHudValue(contract, snap.bossArenasCleared) : "",
      `+${Math.round(raidBonus * 100)}%`,
    ];
    for (let i = 0; i < chips.length; i += 1) {
      const chip = chips[i]!;
      const value = values[i]!;
      if (chip.last !== value) {
        chip.value.textContent = value;
        chip.last = value;
      }
    }
  }

  private renderObjective(snap: WorldSnapshot, banked: BankedProfile): void {
    const el = this.objectiveEl;
    if (!el) return;
    const inRun =
      snap.phase === "expedition" ||
      snap.phase === "onboarding" ||
      snap.phase === "extracting" ||
      snap.phase === "launching";
    if (!inRun) {
      el.hidden = true;
      this.lastObjectiveKey = "";
      return;
    }
    const obj = currentObjective(snap, banked);
    const key = `${obj.tone}|${obj.title}|${obj.detail}`;
    el.hidden = false;
    if (key === this.lastObjectiveKey) return;
    this.lastObjectiveKey = key;
    el.dataset.tone = obj.tone;
    el.innerHTML = `<em>${obj.title}</em><strong>${obj.detail}</strong>`;
  }
  private renderWeaponRail(snap: WorldSnapshot): void {
    const inRun =
      snap.phase === "expedition" ||
      snap.phase === "onboarding" ||
      snap.phase === "extracting" ||
      snap.phase === "launching";
    if (!inRun) {
      this.root.loadoutRail.hidden = true;
      return;
    }
    this.root.loadoutRail.hidden = false;
    const colorblind = snap.settings.colorblindTelegraphs;
    const reduced = snap.settings.reducedMotion;

    if (!this.railBuilt || this.root.loadoutRail.childElementCount !== 3) {
      this.root.loadoutRail.replaceChildren(
        ...(["primary", "secondary", "ordnance"] as const).map((slot) => {
          const cell = document.createElement("div");
          cell.className = "weapon-rail-slot";
          cell.dataset.slot = slot;
          cell.innerHTML = `
            <span class="weapon-rail-shape" aria-hidden="true">${SLOT_SHAPE[slot]}</span>
            <div class="weapon-rail-meta">
              <span class="weapon-rail-name"></span>
              <span class="weapon-rail-key"></span>
              <span class="weapon-rail-mods"></span>
            </div>
            <div class="weapon-rail-cd" aria-hidden="true"><i></i></div>
          `;
          return cell;
        }),
      );
      this.railBuilt = true;
    }

    const slots = snap.weaponSlots;
    for (const slot of slots) {
      const cell = this.root.loadoutRail.querySelector<HTMLElement>(
        `.weapon-rail-slot[data-slot="${slot.slot}"]`,
      );
      if (!cell) continue;
      const empty = slot.weaponId == null;
      cell.dataset.empty = empty ? "true" : "false";
      cell.dataset.ready = slot.ready ? "true" : "false";
      cell.dataset.colorblind = colorblind ? "true" : "false";
      if (reduced) cell.dataset.reduced = "true";
      else delete cell.dataset.reduced;
      const name = cell.querySelector(".weapon-rail-name");
      const key = cell.querySelector(".weapon-rail-key");
      const modsEl = cell.querySelector(".weapon-rail-mods");
      const fill = cell.querySelector<HTMLElement>(".weapon-rail-cd i");
      const nameText = empty ? "—" : slot.shortName;
      if (name && name.textContent !== nameText) name.textContent = nameText;
      if (key && key.textContent !== slot.keyGlyph) key.textContent = slot.keyGlyph;
      if (modsEl) {
        const mods = slot.mods ?? [];
        let modsText = "";
        if (mods.length > 0) {
          const bits = mods.map((m) => `${m.chip}${m.stacks > 1 ? `×${m.stacks}` : ""}`);
          if (slot.homing) bits.push("HOM");
          modsText = bits.join(" ");
        }
        if (modsEl.textContent !== modsText) modsEl.textContent = modsText;
      }
      if (fill) {
        fill.style.transform = `scaleX(${clamp01(slot.cooldownFill)})`;
      }
      const modTitle =
        (slot.mods ?? []).length > 0
          ? ` · ${(slot.mods ?? []).map((m) => `${m.chip}×${m.stacks}`).join(" ")}${slot.homing ? " · HOMING" : ""}`
          : "";
      cell.title = empty
        ? `${slot.slot}: empty`
        : `${slot.shortName} · ${slot.keyGlyph}${slot.ready ? " · ready" : ` · ${slot.cooldownRemaining.toFixed(1)}s`}${modTitle}`;
      cell.dataset.homing = slot.homing ? "true" : "false";
    }
  }

  private renderLoadoutChips(snap: WorldSnapshot): void {
    const inRun =
      snap.phase === "expedition" ||
      snap.phase === "onboarding" ||
      snap.phase === "extracting" ||
      snap.phase === "launching";
    if (!inRun) {
      if (!this.root.affixChips.hidden) {
        this.root.affixChips.hidden = true;
        this.root.affixChips.replaceChildren();
        this.lastAffixKey = "";
      }
      return;
    }

    const affixChips = activeAffixChips(snap.runInventory.affixes);
    const key = [
      snap.runInventory.components.join(","),
      snap.runInventory.affixes.join(","),
      affixChips.map((c) => `${c.chip}:${c.active ? 1 : 0}`).join(","),
    ].join("|");
    if (key === this.lastAffixKey) {
      this.root.affixChips.hidden = this.root.affixChips.childElementCount === 0;
      return;
    }
    this.lastAffixKey = key;

    const nodes: HTMLElement[] = [];

    for (const id of snap.runInventory.components) {
      const def = COMPONENTS.find((c) => c.id === id);
      const el = document.createElement("span");
      el.className = "affix-chip component-chip";
      el.textContent = shortComponent(def?.name ?? id);
      el.title = def ? `${def.name} — ${def.description}` : id;
      el.dataset.kind = "component";
      nodes.push(el);
    }

    if (affixChips.length > 0) {
      for (const chip of affixChips) {
        const el = document.createElement("span");
        el.className = "affix-chip";
        el.textContent = chip.chip;
        el.style.setProperty("--chip-color", chip.css);
        el.dataset.active = chip.active ? "true" : "false";
        el.dataset.kind = "affix";
        el.title = chip.id;
        nodes.push(el);
      }
    } else {
      for (const id of snap.runInventory.affixes.slice(0, 4)) {
        const def = AFFIXES.find((a) => a.id === id);
        const el = document.createElement("span");
        el.className = "affix-chip";
        el.textContent = shortAffix(def?.name ?? id);
        el.title = def ? `${def.name} — ${def.description}` : id;
        el.dataset.kind = "affix";
        el.dataset.active = "true";
        nodes.push(el);
      }
    }

    if (nodes.length === 0) {
      this.root.affixChips.hidden = true;
      this.root.affixChips.replaceChildren();
      return;
    }
    const visible = nodes.slice(0, 4);
    if (nodes.length > visible.length) {
      const overflow = document.createElement("span");
      overflow.className = "affix-chip affix-chip-overflow";
      overflow.textContent = `+${nodes.length - visible.length}`;
      overflow.title = `${nodes.length - visible.length} more active build items`;
      visible.push(overflow);
    }
    this.root.affixChips.hidden = false;
    this.root.affixChips.replaceChildren(...visible);
  }

  private renderSoftLockReticle(snap: WorldSnapshot): void {
    const reticle = this.root.softLockReticle;
    const locked = snap.assistTargetId != null;

    if (!locked) {
      reticle.hidden = true;
      delete reticle.dataset.pulse;
      delete reticle.dataset.tint;
      return;
    }

    reticle.hidden = false;
    if (snap.settings.colorblindTelegraphs) reticle.dataset.colorblind = "true";
    else delete reticle.dataset.colorblind;
    if (snap.softLockPulse && !snap.settings.reducedMotion) {
      reticle.dataset.pulse = "true";
    } else {
      delete reticle.dataset.pulse;
    }
  }

  private renderOutcomeBeat(snap: WorldSnapshot, hold: boolean): void {
    const beat = this.root.outcomeBeat;
    const show =
      hold && (snap.phase === "dead" || snap.phase === "extracted") && snap.runSummary != null;
    if (!show) {
      beat.hidden = true;
      this.lastOutcomeKey = "";
      return;
    }
    const outcomeKey = `${snap.phase}|${snap.runSummary?.grade ?? ""}|${snap.runSummary?.salvageBanked ?? 0}|${snap.lastDamageSource?.attackKind ?? ""}`;
    beat.hidden = false;
    beat.dataset.outcome = snap.phase;
    if (outcomeKey === this.lastOutcomeKey) return;
    this.lastOutcomeKey = outcomeKey;
    const title = beat.querySelector<HTMLElement>("[data-outcome-title]");
    const detail = beat.querySelector<HTMLElement>("[data-outcome-detail]");
    if (snap.phase === "dead") {
      const report = snap.runSummary?.deathReport ?? snap.lastDamageSource;
      const summary = snap.runSummary;
      if (title) {
        title.textContent = summary ? debriefHeroCopy(summary).headline : "HULL BREACH";
      }
      if (detail) {
        detail.textContent = report
          ? `${formatDeathHeadline(report)} · ${formatDeathDetail(report)} · Grade ${summary?.grade ?? "?"}`
          : snap.message;
      }
    } else {
      const banked = snap.runSummary?.salvageBanked ?? 0;
      if (title) {
        title.textContent = snap.runSummary
          ? debriefHeroCopy(snap.runSummary).headline
          : "EXTRACT SECURE";
      }
      if (detail) {
        detail.textContent = `+${banked} salvage banked · Grade ${snap.runSummary?.grade ?? "?"}`;
        detail.dataset.countTarget = String(banked);
      }
    }
  }

  private renderCarrierNav(snap: WorldSnapshot): void {
    const nav = snap.carrierNav;
    const el = this.root.carrierNav;
    const poi = snap.nearestPoi;
    const showBossTip =
      !nav?.active &&
      !snap.paused &&
      poi?.kind === "boss_arena" &&
      poi.distanceM < 120 &&
      (snap.phase === "expedition" || snap.phase === "onboarding" || snap.phase === "extracting");

    if (nav?.active && !snap.paused) {
      el.hidden = false;
      if (el.dataset.kind) delete el.dataset.kind;
      const screenDeg = 90 - (nav.bearing * 180) / Math.PI;
      if (screenDeg !== this.lastCarrierDeg) {
        el.style.setProperty("--bearing", `${screenDeg}deg`);
        const arrow = el.querySelector(".carrier-nav-arrow");
        if (arrow instanceof HTMLElement) arrow.style.setProperty("--bearing", `${screenDeg}deg`);
        this.lastCarrierDeg = screenDeg;
      }
      if (nav.label !== this.lastCarrierLabel) {
        const text = el.querySelector(".carrier-nav-text");
        if (text) text.textContent = nav.label;
        this.lastCarrierLabel = nav.label;
      }
      return;
    }

    if (showBossTip && poi) {
      el.hidden = false;
      el.dataset.kind = "boss_arena";
      const screenDeg = 90 - (poi.bearing * 180) / Math.PI;
      if (screenDeg !== this.lastCarrierDeg) {
        el.style.setProperty("--bearing", `${screenDeg}deg`);
        const arrow = el.querySelector(".carrier-nav-arrow");
        if (arrow instanceof HTMLElement) arrow.style.setProperty("--bearing", `${screenDeg}deg`);
        this.lastCarrierDeg = screenDeg;
      }
      const label = `BOSS ARENA ${Math.round(poi.distanceM)}m`;
      if (label !== this.lastCarrierLabel) {
        const text = el.querySelector(".carrier-nav-text");
        if (text) text.textContent = label;
        this.lastCarrierLabel = label;
      }
      return;
    }

    el.hidden = true;
    if (el.dataset.kind) delete el.dataset.kind;
  }
}

function clampPct(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function shortComponent(name: string): string {
  const map: Record<string, string> = {
    "Plate Kit": "PLT",
    "Capacitor Bank": "CAP",
    "Boost Cell": "BST",
    "Beacon Link": "BCN",
  };
  return map[name] ?? name.slice(0, 4).toUpperCase();
}

function shortAffix(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0] ?? "")
    .join("")
    .slice(0, 4)
    .toUpperCase();
}
